import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TablePaginationOwnProps,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { rowsPerPage } from '../../../../utils/const';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { ReactComponent as SettingWheel } from '../../../../components/icon/SettingWheel.svg';
import { formatDateTime } from '../../../../utils/formatterHelper';
import { AppPageNameRoutes } from '../../../../routes/paths';
import { StyledTableCell, StyledTableRow } from '../../../../components';
import { ImportStateEnum } from '../../../../api/graphql/generated/schema';
import { FiberManualRecord, OpenInNew } from '@mui/icons-material';
import { ImportedVehiclesQuery } from '../../../../api/graphql/generated/operations';

type Props = {
  dataPage: ImportedVehiclesQuery['getImportedVehicleDefinitions'] | null | undefined;
  handleChangePage: TablePaginationOwnProps['onPageChange'];
  handleChangeRowsPerPage: TablePaginationOwnProps['onRowsPerPageChange'];
};

function ImportedVehicleDefinitionTable({ dataPage, handleChangePage, handleChangeRowsPerPage }: Props) {
  const { t } = useTranslation();
  const history = useHistory();

  const transmissionColumnTranslation = t('fieldName.transmission');

  const redirectToEdit = (event: React.MouseEvent, vin: string) => {
    const newUrl = AppPageNameRoutes.IMPORTED_VEHICLE_DEFINITIONS_EDIT.pageRoute.replace(':importedVehicleVin', vin);
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      window.open(newUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    history.push(newUrl);
  };

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const stateIconHandler = (state: ImportStateEnum) => {
    let tooltipTitle = t('importedVehicle.states.' + state);
    let className = '';
    switch (state) {
      case ImportStateEnum.CardAwaitingApproval:
        className = 'orange-dot';
        break;
      case ImportStateEnum.CardPublished:
        className = 'green-dot';
        break;
      case ImportStateEnum.NoCard:
        className = 'red-dot';
        break;
    }
    return (
      <Tooltip title={tooltipTitle}>
        <FiberManualRecord className={className} />
      </Tooltip>
    );
  };

  return (
    <Card
      variant='outlined'
      className='card-overview'
    >
      <TableContainer component={Paper}>
        <Table
          aria-label='customized table'
          className='dataTable__holder'
        >
          <TableHead>
            <TableRow>
              <StyledTableCell align='center'>{t('fieldName.state')}</StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.vin')}</StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.brand')}</StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.line')}</StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.modelYear')}</StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.model')}</StyledTableCell>
              <StyledTableCell align='center'>
                <Tooltip title={transmissionColumnTranslation}>
                  <SettingWheel />
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.color')}</StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.imported')}</StyledTableCell>
              <StyledTableCell align='center'>{t('fieldName.importedFrom')}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!dataPage || !dataPage.data || dataPage.data.length === 0) && (
              <Typography variant={'subtitle1'}>{t('datatable.empty')}</Typography>
            )}
            {dataPage?.data.map((row) => (
              <StyledTableRow
                key={row?.vin}
                onClick={(event) => redirectToEdit(event, row.vin)}
              >
                <StyledTableCell align='center'>{stateIconHandler(row.importState)}</StyledTableCell>
                <StyledTableCell align='center'>{row?.vin}</StyledTableCell>
                <StyledTableCell align='center'>{row?.brandId}</StyledTableCell>
                <StyledTableCell align='center'>{row?.line}</StyledTableCell>
                <StyledTableCell align='center'>{row?.modelYear}</StyledTableCell>
                <StyledTableCell align='center'>{row?.model}</StyledTableCell>
                <StyledTableCell align='center'>{row?.transmissionType}</StyledTableCell>
                <StyledTableCell align='center'>{row?.color}</StyledTableCell>
                <StyledTableCell align='center'>
                  <Tooltip title={row?.imported}>
                    <span>{formatDateTime(row?.imported)}</span>
                  </Tooltip>
                </StyledTableCell>
                <StyledTableCell align='center'>
                  {row.importAdvertisementUrl ? (
                    <Button
                      href={row.importAdvertisementUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      endIcon={<OpenInNew />}
                      onClick={stopPropagation}
                    >
                      {row.importedFromSystem}
                    </Button>
                  ) : (
                    row.importedFromSystem
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {dataPage?.pageInfo && (
        <TablePagination
          component={'div'}
          rowsPerPageOptions={rowsPerPage}
          labelRowsPerPage={t('datatable.paging')}
          count={dataPage.pageInfo.totalCount ? dataPage.pageInfo.totalCount : 0}
          rowsPerPage={dataPage.pageInfo.limit ? dataPage.pageInfo.limit : 0}
          page={dataPage.pageInfo.offset ? dataPage.pageInfo.offset / (dataPage.pageInfo.limit || 1) : 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      )}
    </Card>
  );
}

export default ImportedVehicleDefinitionTable;
